/** App routes + on-chain constants for IDX Launchlab (see scripts/idx-launchlab/DEV-REFERENCE.md). */

import { communitySignalPagePath } from "@/lib/community/communitySignalUrls";

export const IDX_LAUNCHLAB_MARKETING_PATH = "/idx-launchlab";
export const IDX_LAUNCHLAB_LIVE_PATH = "/idx-launchlab/live";
export const IDX_LAUNCHLAB_CREATE_PATH = "/idx-launchlab/create";
export const IDX_LAUNCHLAB_REWARDS_PATH = "/idx-launchlab/rewards";
/** @deprecated Use IDX_LAUNCHLAB_REWARDS_PATH — kept for redirects */
export const IDX_LAUNCHLAB_PLATFORM_FEES_PATH = "/idx-launchlab/platform-fees";

/** Min claimable balance (lamports) before Withdraw is enabled */
export const IDX_LAUNCHLAB_REWARDS_DUST_LAMPORTS = 10_000;

/** Min wallet SOL (lamports) required to sign claim / harvest transactions */
export const IDX_LAUNCHLAB_CLAIM_MIN_SOL_LAMPORTS = 5_000_000;

/** Typical create-only wallet charge (rent + fees). Shown in UI; exact amount is in the wallet prompt. */
export const LAUNCHLAB_ESTIMATED_CREATE_FEE_SOL = 0.025;

/**
 * First buy during launch. Off while Phantom flags the buy request that follows a
 * create ("This dApp could be malicious") — creators buy from their coin page instead.
 * Turn back on once the wallet warning is resolved; the sequential create→buy path is intact.
 */
export const LAUNCHLAB_FIRST_BUY_ENABLED = false;

export function idxLaunchlabTokenPath(mint, tokenNameOrSymbol) {
  const trimmed = typeof mint === "string" ? mint.trim() : "";
  if (!trimmed) return IDX_LAUNCHLAB_LIVE_PATH;
  return communitySignalPagePath(trimmed, tokenNameOrSymbol || "token");
}

/** LaunchLab program IDs — verify on mainnet before production deploy */
export const IDX_LAUNCHLAB_PROGRAM_IDS = {
  mainnet: "LanMV9sAd7wArD4vJFi2qDdfnVhFxYSUg6eADduJ3uj",
  devnet: "DRay6fNdQ5J82H7xV6uq2aV3mNrUZ1J4PgSKsWgptcm6",
};

/** SOL constant-product GlobalConfig (mainnet) from Raydium launch configs API */
export const IDX_LAUNCHLAB_DEFAULT_CONFIG_ID_MAINNET =
  "6s1xP3hpbAfFoNtUNF8mfHsjr2Bd97JxFJRWLbL6aHuX";

export const IDX_LAUNCHLAB_CPMM_CONFIG_INDEX = Number.parseInt(
  process.env.RAYDIUM_CPMM_CONFIG_INDEX ?? "0",
  10,
);

/** Registered IDX PlatformConfig PDA — set after CreatePlatformConfig */
export function getIdxLaunchlabPlatformPda() {
  return process.env.NEXT_PUBLIC_IDX_LAUNCHLAB_PLATFORM_PDA?.trim() ?? "";
}

/** IDX company wallet for platform admin / fee claims (public) */
export { IDX_LAUNCHLAB_PLATFORM_WALLET } from "@/lib/idx-launchlab/platform-on-chain-config";

/** Comma-separated pubkeys allowed on platform-fees page */
export function getIdxLaunchlabPlatformAdmins() {
  const raw =
    process.env.NEXT_PUBLIC_IDX_LAUNCHLAB_PLATFORM_ADMIN?.trim() ??
    process.env.IDX_LAUNCHLAB_PLATFORM_ADMIN?.trim() ??
    "";
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function isIdxLaunchlabPlatformAdmin(pubkey) {
  if (!pubkey) return false;
  const admins = getIdxLaunchlabPlatformAdmins();
  if (!admins.length) return false;
  return admins.includes(pubkey);
}

/** platform_cp_creator = default (creator owns CPMM creator fees) */
export const IDX_LAUNCHLAB_PLATFORM_CP_CREATOR_DEFAULT =
  "11111111111111111111111111111111";

/** Raydium LaunchLab list sorts — proxied via buildLaunchListUrl() (not bare ?sort= alone). */
export const IDX_LAUNCHLAB_FEED_SORTS = [
  { id: "hotToken", label: "Trending" },
  { id: "new", label: "New" },
  { id: "marketCap", label: "Market cap" },
  { id: "lastTrade", label: "Last trade" },
];

export const idxLaunchlabAppConfig = {
  marketingPath: IDX_LAUNCHLAB_MARKETING_PATH,
  livePath: IDX_LAUNCHLAB_LIVE_PATH,
  createPath: IDX_LAUNCHLAB_CREATE_PATH,
  rewardsPath: IDX_LAUNCHLAB_REWARDS_PATH,
  platformFeesPath: IDX_LAUNCHLAB_REWARDS_PATH,
  /** Batched refresh for viewport-visible feed cards only (one API call per tick). */
  visibleFeedRefreshMs: 5_000,
  /** Min gap between scroll pagination requests (avoids Raydium 429). */
  feedLoadMoreMinMs: 1000,
  tokenPollMs: 20_000,
  /** Server-side Raydium list cache; serve stale on upstream errors (429, etc.). */
  feedCacheMs: 45_000,
  /** Tokens shown per live-feed page (pagination). */
  feedScrollPageSize: 60,
  /** Raydium has no total field — we paginate all list pages; refresh on this interval. */
  listedTokenCountCacheMs: 30 * 60 * 1000,
  defaultListSort: "hotToken",
  feedSorts: IDX_LAUNCHLAB_FEED_SORTS,
  /** IDX Last trade tab pin window — watcher + feed use the same value. */
  lastTradePinWindowMs: 60 * 60 * 1000,
  /** Trending and New pin IDX coins only while they are this new. */
  idxCreatedPinWindowMs: 24 * 60 * 60 * 1000,
  /** Backend trade watcher active-set cap (mints polled per tick). */
  tradeWatcherActiveMintCap: 100,
  /** Launches younger than this stay on the watcher active set. */
  tradeWatcherActiveLaunchDays: 14,
  /** Skip silent tab revalidate if cache is newer than this (ms). */
  feedTabRevalidateMs: 45_000,
  /** Stagger delay between idle prefetch of other sort tabs (ms). */
  feedTabPrefetchStaggerMs: 3_000,
};
